import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../config/supabase';
import { X, Upload, Loader2, RefreshCw, Tag, MapPin, Globe, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { triggerKeywordGeneration } from '../../services/keywordService';
import { v4 as uuidv4 } from 'uuid';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useForm } from 'react-hook-form';
import { traderService, TraderFeatures } from '../../services/traderService';
import { SocialLinks, getSocialLinksByUserId } from '../../services/socialLinksService';
import { compressImage, AVATAR_PRESET, validateImageFile, formatFileSize } from '../../utils/imageCompression';

interface Trader {
  id: number;
  user_id?: string;
  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  description: string;
  profession_id: number;
  avatar_url?: string;
  rating?: number;
  reviews?: number;
  verified: boolean;
  created_at: string;
  updated_at: string;
  website?: string;
  keywords?: string[];
  business_type_id?: string;
  show_map?: boolean; // Map visibility toggle
}

interface EditTraderFormProps {
  trader: Trader;
  sourceTable?: 'traders' | 'hospitality';
  onClose: () => void;
  onSave: (updatedTrader: Trader) => void;
}

const EditTraderForm: React.FC<EditTraderFormProps> = ({ trader, sourceTable = 'traders', onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(trader?.avatar_url || null);
  const avatarPreviewObjectUrlRef = useRef<string | null>(null);
  const [isCompressingAvatar, setIsCompressingAvatar] = useState(false);
  const [avatarCompressionInfo, setAvatarCompressionInfo] = useState<string | null>(null);
  const [professions, setProfessions] = useState<{ id: number; name: string; description: string; category?: string | null }[]>([]);
  const [isRegeneratingKeywords, setIsRegeneratingKeywords] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<{ id: string; name: string }[]>([]);
  const [selectedBusinessTypeId, setSelectedBusinessTypeId] = useState<string | undefined>(trader.business_type_id);

  const [selectedTradeCategory, setSelectedTradeCategory] = useState<string>('');
  const [tradeCategories, setTradeCategories] = useState<string[]>([]);
  const [selectedBusinessTypeName, setSelectedBusinessTypeName] = useState<string>('');
  
  // Map visibility state
  const [showMap, setShowMap] = useState<boolean>(trader.show_map !== false);
  
  // Social links state
  const [socialLinks, setSocialLinks] = useState<SocialLinks | null>(null);
  const [savingSocialLinks, setSavingSocialLinks] = useState(false);
  
  // Trader features state
  const [traderFeatures, setTraderFeatures] = useState<TraderFeatures | null>(null);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featureImageFiles, setFeatureImageFiles] = useState<{
    feature1: File | null;
    feature2: File | null;
    feature3: File | null;
  }>({
    feature1: null,
    feature2: null,
    feature3: null
  });

  const [featureImagePreviews, setFeatureImagePreviews] = useState<{
    feature1: string | null;
    feature2: string | null;
    feature3: string | null;
  }>({
    feature1: null,
    feature2: null,
    feature3: null
  });
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    socialLinks: false,
    features: false,
    mapSettings: false,
    keywords: false
  });
  
  // Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Fetch business types
  useEffect(() => {
    const fetchBusinessTypes = async () => {
      try {
        const { data, error } = await supabase
          .from('business_types')
          .select('id, name')
          .order('name');

        if (error) {
          console.error('Error fetching business types for edit form:', error);
          return;
        }

        if (data) {
          setBusinessTypes(data);
        }
      } catch (err) {
        console.error('Failed to fetch business types for edit form:', err);
      }
    };

    if (sourceTable === 'traders') {
      fetchBusinessTypes();
    }
  }, [sourceTable]);

  useEffect(() => {
    if (sourceTable !== 'traders') return;

    const btId = selectedBusinessTypeId || trader.business_type_id;
    const btName = businessTypes.find((bt) => bt.id === btId)?.name?.toLowerCase() || '';
    setSelectedBusinessTypeName(btName);
  }, [businessTypes, selectedBusinessTypeId, sourceTable, trader.business_type_id]);

  const isTradesman = sourceTable === 'traders' && selectedBusinessTypeName === 'tradesman';

  useEffect(() => {
    const fetchTradeCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('professions')
          .select('category')
          .order('category');

        if (error) {
          console.error('Error fetching trade categories:', error);
          setTradeCategories([]);
          return;
        }

        const uniqueCategories = Array.from(
          new Set((data || []).map((row: any) => String(row.category || '').trim()).filter(Boolean))
        );

        setTradeCategories(uniqueCategories);
      } catch (err) {
        console.error('Failed to fetch trade categories:', err);
        setTradeCategories([]);
      }
    };

    if (isTradesman) {
      fetchTradeCategories();
    } else {
      setTradeCategories([]);
      setSelectedTradeCategory('');
    }
  }, [isTradesman]);

  useEffect(() => {
    const hydrateSelectedTradeCategory = async () => {
      if (!isTradesman) return;
      if (selectedTradeCategory) return;
      if (!trader.profession_id) return;

      try {
        const { data, error } = await supabase
          .from('professions')
          .select('category')
          .eq('id', trader.profession_id)
          .single();

        if (error) {
          console.error('Error fetching current trade category:', error);
          return;
        }

        const category = String((data as any)?.category || '').trim();
        if (category) {
          setSelectedTradeCategory(category);
        }
      } catch (err) {
        console.error('Failed to hydrate current trade category:', err);
      }
    };

    hydrateSelectedTradeCategory();
  }, [isTradesman, selectedTradeCategory, trader.profession_id]);

  useEffect(() => {
    return () => {
      if (avatarPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
        avatarPreviewObjectUrlRef.current = null;
      }
    };
  }, []);
  
  // Fetch social links and features if trader has user_id
  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!trader.user_id) return;
      
      try {
        // Fetch social links
        const links = await getSocialLinksByUserId(trader.user_id);
        setSocialLinks(links);
        
        // Fetch trader features
        const features = await traderService.getTraderFeatures(trader.user_id);
        if (features) {
          setTraderFeatures(features);
        } else {
          // Set default features
          setTraderFeatures({
            user_id: trader.user_id,
            feature1_enabled: true,
            feature1_title: trader.store_name || 'Our Business',
            feature1_description: trader.description || 'Discover our unique offerings',
            feature1_image: trader.avatar_url || '/bally-Market-sign-200px.png',
            feature2_enabled: false,
            feature2_title: '',
            feature2_description: '',
            feature2_image: '',
            feature3_enabled: false,
            feature3_title: '',
            feature3_description: '',
            feature3_image: '',
            features_section_title: 'Featured Offerings',
            features_layout: 'rows'
          });
        }
      } catch (err) {
        console.error('Error fetching additional trader data:', err);
      }
    };
    
    fetchAdditionalData();
  }, [trader.user_id, trader.store_name, trader.description, trader.avatar_url]);

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        // Default to trader professions
        let tableName = 'professions';

        // If we know the business type, use the matching professions table
        const businessTypeIdToUse = selectedBusinessTypeId || trader.business_type_id;

        if (businessTypeIdToUse) {
          const { data: businessType, error: btError } = await supabase
            .from('business_types')
            .select('name')
            .eq('id', businessTypeIdToUse)
            .single();

          if (btError) {
            console.error('Error fetching business type for trader:', btError);
          } else if (businessType?.name) {
            const businessTypeName = businessType.name.toLowerCase();

            if (businessTypeName === 'craftsman') {
              tableName = 'craft_professions';
            } else if (businessTypeName === 'hospitality') {
              tableName = 'hospitality_categories';
            } else if (businessTypeName && businessTypeName.includes('professional')) {
              tableName = 'professionals_categories';
            } else {
              tableName = 'professions'; // tradesman or default
            }
          }
        }

        let query = supabase
          .from(tableName)
          .select(tableName === 'professions' ? 'id, name, description, category' : 'id, name, description')
          .order('name');

        // For tradesman, allow filtering the professions list by selected Type A category.
        if (tableName === 'professions' && selectedTradeCategory) {
          query = query.eq('category', selectedTradeCategory);
        }

        const { data, error } = await query;

        if (error) {
          console.error(`Error fetching professions from ${tableName}:`, error);
          return;
        }

        if (data) {
          setProfessions(data as any);
        }
      } catch (err) {
        console.error('Failed to fetch professions for edit form:', err);
      }
    };

    fetchProfessions();
  }, [trader.business_type_id, selectedBusinessTypeId, selectedTradeCategory]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset
  } = useForm<Trader>({
    defaultValues: {
      ...trader,
      profession_id: trader.profession_id // Set default profession
    },
    mode: 'onChange'
  });

  useEffect(() => {
    reset({
      ...trader,
      profession_id: trader.profession_id,
    });
    setSelectedBusinessTypeId(trader.business_type_id);
    setShowMap(trader.show_map !== false);
    setAvatarFile(null);
    if (avatarPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
      avatarPreviewObjectUrlRef.current = null;
    }
    setAvatarPreview(trader?.avatar_url || null);
  }, [trader.id, reset]);

  const watchedProfessionId = watch('profession_id');

  const handleProfessionSelect = (value: string) => {
    setValue('profession_id', parseInt(value));
  };

  // Prevent the UI from getting stuck forever if the update request hangs/gets aborted.
  const withTimeout = async <T,>(promise: PromiseLike<T>, timeoutMs: number, label: string): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`));
      }, timeoutMs);
    });

    try {
      return await Promise.race([Promise.resolve(promise), timeoutPromise]);
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  const onSubmit = async (data: Trader) => {
    if (loading || isSubmitting) return;

    const tableName = sourceTable || 'traders';

    try {
      setLoading(true);
      setError('');

      // Validate required fields
      if (!data.store_name?.trim()) {
        setError('Store name is required');
        return;
      }

      console.log('Submitting data:', data);

      // Upload avatar if new file selected
      let avatarUrl = trader?.avatar_url;
      if (avatarFile) {
        try {
          console.log('Uploading new avatar...');
          avatarUrl = await uploadAvatar();
          console.log('Avatar upload completed:', avatarUrl);
        } catch (uploadError) {
          console.error('Avatar upload failed:', uploadError);
          setError(`Avatar upload failed: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
          return;
        }
      }

      // Prepare update payload
      const updatePayload: any = {
        store_name: data.store_name.trim(),
        owner_name: data.owner_name.trim(),
        email: data.email.trim(),
        phone_number: data.phone_number.trim(),
        location: data.location.trim(),
        description: data.description.trim(),
        profession_id: data.profession_id,
        website: data.website?.trim(), // Add website field
        avatar_url: avatarUrl,
        updated_at: new Date().toISOString()
      };

      // Only traders table has editable business_type_id in this form
      if (tableName === 'traders') {
        updatePayload.business_type_id = data.business_type_id || selectedBusinessTypeId || trader.business_type_id;
        updatePayload.show_map = showMap;
      }

      // Update trader data - use exact column names from database
      const updateResult = await withTimeout<any>(
        supabase
          .from(tableName)
          .update(updatePayload)
          .eq('id', trader.id)
          .select('*')
          .single(),
        65000,
        `Database update (${tableName})`
      );

      const { data: updatedTrader, error: updateError } = updateResult;

      if (updateError) {
        console.error('Update error:', updateError);
        setError(`Failed to update trader: ${updateError.message}`);
        return;
      }

      
      if (!updatedTrader) {
        throw new Error('No data returned after update');
      }

      console.log('Successfully updated trader:', updatedTrader);
      onSave(updatedTrader);
      onClose();
    } catch (err) {
      console.error('Error updating trader:', err);
      setError(err instanceof Error ? err.message : 'Failed to update trader');
    } finally {
      setLoading(false);
    }
  };

  const uploadAvatar = async (): Promise<string> => {
    if (!avatarFile) return trader?.avatar_url || '';

    try {
      console.log('Starting avatar upload for trader:', trader.id);
      console.log('File details:', {
        name: avatarFile.name,
        size: avatarFile.size,
        type: avatarFile.type
      });

      // Validate file size (max 5MB)
      if (avatarFile.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Validate file type
      if (!avatarFile.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Create unique file path
      const fileExt = avatarFile.name.split('.').pop()?.toLowerCase();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `traders/${trader.id}/${fileName}`;

      console.log('Upload path:', filePath);

      // First, try to remove any existing avatar for this trader
      try {
        const { data: existingFiles } = await supabase.storage
          .from('avatars')
          .list(`traders/${trader.id}`);
        
        if (existingFiles && existingFiles.length > 0) {
          console.log('Removing existing files:', existingFiles.length);
          const filesToRemove = existingFiles.map(file => `traders/${trader.id}/${file.name}`);
          await supabase.storage
            .from('avatars')
            .remove(filesToRemove);
        }
      } catch (cleanupError) {
        console.warn('Could not clean up existing files:', cleanupError);
        // Continue with upload even if cleanup fails
      }

      // Upload file with detailed error handling
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true,
          contentType: avatarFile.type
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      console.log('Upload successful:', uploadData);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      console.log('Generated public URL:', publicUrl);

      if (!publicUrl) {
        throw new Error('Failed to generate public URL');
      }

      return publicUrl;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload avatar';
      throw new Error(errorMessage);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressingAvatar(true);
      setAvatarCompressionInfo(null);
      setError('');

      const validation = validateImageFile(file, 20);
      if (!validation.isValid) {
        setError(validation.error ?? 'Please select a smaller image file.');
        return;
      }

      const originalSize = file.size;
      const compressedFile = await compressImage(file, AVATAR_PRESET);
      const compressedSize = compressedFile.size;
      const reduction =
        originalSize > 0
          ? (((originalSize - compressedSize) / originalSize) * 100).toFixed(0)
          : '0';

      setAvatarFile(compressedFile);
      if (avatarPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
        avatarPreviewObjectUrlRef.current = null;
      }
      const nextPreviewUrl = URL.createObjectURL(compressedFile);
      avatarPreviewObjectUrlRef.current = nextPreviewUrl;
      setAvatarPreview(nextPreviewUrl);
      setAvatarCompressionInfo(
        `Image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
          compressedSize
        )} (${reduction}% smaller)`
      );
    } catch (err) {
      console.error('Error processing avatar image:', err);
      setError('Failed to process avatar image. Please try another file.');
    } finally {
      setIsCompressingAvatar(false);
    }
  };

  const handleRegenerateKeywords = async () => {
    setIsRegeneratingKeywords(true);
    try {
      const success = await triggerKeywordGeneration(
        trader.id,
        'trader',
        {
          store_name: trader.store_name,
          description: trader.description,
          location: trader.location
        }
      );
      
      if (success) {
        setError('');
        alert('✅ Keyword generation started! Keywords will be updated in a few moments.');
      } else {
        setError('Failed to trigger keyword generation');
      }
    } catch (err) {
      console.error('Error triggering keyword generation:', err);
      setError('Failed to trigger keyword generation');
    } finally {
      setIsRegeneratingKeywords(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 flex-shrink-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {trader?.id ? 'Edit Trader' : 'Add New Trader'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 pt-0 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="store_name">Store Name*</Label>
                <Input
                  id="store_name"
                  {...register('store_name', { 
                    required: 'Store name is required',
                    minLength: { value: 2, message: 'Store name must be at least 2 characters' }
                  })}
                  className={errors.store_name ? 'border-red-500' : ''}
                />
                {errors.store_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.store_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="owner_name">Owner Name*</Label>
                <Input
                  id="owner_name"
                  {...register('owner_name', { required: 'Owner name is required' })}
                  className={errors.owner_name ? 'border-red-500' : ''}
                />
                {errors.owner_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.owner_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email*</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone_number">Phone Number*</Label>
                <Input
                  id="phone_number"
                  {...register('phone_number', { required: 'Phone number is required' })}
                  className={errors.phone_number ? 'border-red-500' : ''}
                />
                {errors.phone_number && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone_number.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="location">Location*</Label>
                <Input
                  id="location"
                  {...register('location', { required: 'Location is required' })}
                  className={errors.location ? 'border-red-500' : ''}
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-500">{errors.location.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://"
                  {...register('website', {
                    pattern: {
                      value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                      message: 'Please enter a valid URL'
                    }
                  })}
                  className={errors.website ? 'border-red-500' : ''}
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-500">{errors.website.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description*</Label>
                <Textarea
                  id="description"
                  {...register('description', { required: 'Description is required' })}
                  className={errors.description ? 'border-red-500' : ''}
                  rows={4}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              <div>
                {sourceTable === 'traders' && (
                  <div className="mb-4">
                    <Label htmlFor="business_type_id">Business Type*</Label>
                    <Select
                      onValueChange={(value) => {
                        setSelectedBusinessTypeId(value);
                        setSelectedBusinessTypeName(
                          businessTypes.find((bt) => bt.id === value)?.name?.toLowerCase() || ''
                        );
                        setValue('business_type_id', value as any);
                        setValue('profession_id', undefined as any);
                        setSelectedTradeCategory('');
                      }}
                      value={selectedBusinessTypeId || trader.business_type_id || undefined}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a business type" />
                      </SelectTrigger>
                      <SelectContent>
                        {businessTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isTradesman && (
                  <div className="mb-4">
                    <Label>Trade Category</Label>
                    <Select
                      onValueChange={(value) => {
                        const nextCategory = value === '__all__' ? '' : value;
                        setSelectedTradeCategory(nextCategory);
                        setValue('profession_id', undefined as any);
                      }}
                      value={selectedTradeCategory || undefined}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select trade category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All categories</SelectItem>
                        {tradeCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Label htmlFor="profession_id">Profession*</Label>
                <Select
                  onValueChange={handleProfessionSelect}
                  value={watchedProfessionId ? String(watchedProfessionId) : undefined}
                >
                  <SelectTrigger className={errors.profession_id ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select a profession" />
                  </SelectTrigger>
                  <SelectContent>
                    {watchedProfessionId && !professions.some((p) => p.id === watchedProfessionId) && (
                      <SelectItem
                        key={`selected-${watchedProfessionId}`}
                        value={String(watchedProfessionId)}
                      >
                        {`Selected (id: ${watchedProfessionId})`}
                      </SelectItem>
                    )}
                    {professions.map((profession) => (
                      <SelectItem
                        key={profession.id}
                        value={String(profession.id)}
                      >
                        {profession.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.profession_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.profession_id.message}</p>
                )}
              </div>

              <div>
                <Label>Avatar</Label>
                <div className="flex items-center space-x-4">
                  <img
                    src={avatarPreview || trader?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${trader?.store_name}`}
                    alt="Avatar preview"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="cursor-pointer bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 flex items-center space-x-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Upload new avatar</span>
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
                {isCompressingAvatar && (
                  <p className="mt-1 text-xs text-gray-500">Optimizing image...</p>
                )}
                {avatarCompressionInfo && !isCompressingAvatar && (
                  <p className="mt-1 text-xs text-gray-600">{avatarCompressionInfo}</p>
                )}
              </div>

              {/* Map Settings Section */}
              {sourceTable === 'traders' && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleSection('mapSettings')}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <MapPin className="w-4 h-4" />
                      Map Settings
                    </Label>
                    {expandedSections.mapSettings ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.mapSettings && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Show map on public profile</p>
                          <p className="text-xs text-gray-500">Toggle to show/hide the location map on the trader's public page</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowMap(!showMap)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                            showMap ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                              showMap ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      {/* Map Preview */}
                      {trader.location && showMap && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-2">Map Preview:</p>
                          <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                            <iframe
                              title={`Map preview for ${trader.store_name}`}
                              src={`https://www.google.com/maps?q=${encodeURIComponent(`${trader.location}, Ireland`)}&output=embed`}
                              width="100%"
                              height="150"
                              style={{ border: 0 }}
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Social Links Section - Only show if trader has user_id */}
              {trader.user_id && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleSection('socialLinks')}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Globe className="w-4 h-4" />
                      Social Media Links
                    </Label>
                    {expandedSections.socialLinks ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.socialLinks && socialLinks && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-4">
                      {/* Show socials toggle */}
                      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Show social media section</p>
                          <p className="text-xs text-gray-500">Toggle visibility on public profile</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSocialLinks(prev => prev ? { ...prev, show_socials: !prev.show_socials } : prev)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                            socialLinks.show_socials !== false ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                              socialLinks.show_socials !== false ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      {/* Social links inputs */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Facebook</label>
                          <Input
                            type="url"
                            value={socialLinks.facebook_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, facebook_url: e.target.value } : prev)}
                            placeholder="https://facebook.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Instagram</label>
                          <Input
                            type="url"
                            value={socialLinks.instagram_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, instagram_url: e.target.value } : prev)}
                            placeholder="https://instagram.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">YouTube</label>
                          <Input
                            type="url"
                            value={socialLinks.youtube_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, youtube_url: e.target.value } : prev)}
                            placeholder="https://youtube.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">LinkedIn</label>
                          <Input
                            type="url"
                            value={socialLinks.linkedin_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, linkedin_url: e.target.value } : prev)}
                            placeholder="https://linkedin.com/..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">TikTok</label>
                          <Input
                            type="url"
                            value={socialLinks.tiktok_url || ''}
                            onChange={(e) => setSocialLinks(prev => prev ? { ...prev, tiktok_url: e.target.value } : prev)}
                            placeholder="https://tiktok.com/..."
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Save social links button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!socialLinks || !trader.user_id) return;
                          setSavingSocialLinks(true);
                          try {
                            // Prepare social links data without duplicating user_id
                            const socialLinksData = {
                              ...socialLinks,
                              user_id: trader.user_id,
                              updated_at: new Date().toISOString()
                            };
                            const { error } = await supabase
                              .from('user_social_links')
                              .upsert(socialLinksData);
                            if (error) throw error;
                            alert('✅ Social links saved successfully!');
                          } catch (err) {
                            console.error('Error saving social links:', err);
                            alert('Failed to save social links');
                          } finally {
                            setSavingSocialLinks(false);
                          }
                        }}
                        disabled={savingSocialLinks}
                        className="w-full"
                      >
                        {savingSocialLinks ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" /> Save Social Links</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Feature Showcase Section - Only show if trader has user_id */}
              {trader.user_id && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleSection('features')}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Tag className="w-4 h-4" />
                      Feature Showcase
                    </Label>
                    {expandedSections.features ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.features && traderFeatures && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-4">
                      {/* Section title */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Section Title</label>
                        <Input
                          type="text"
                          value={traderFeatures.features_section_title || 'Featured Offerings'}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, features_section_title: e.target.value } : prev)}
                          placeholder="Featured Offerings"
                          className="text-sm"
                        />
                      </div>
                      
                      {/* Layout selection */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Layout</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setTraderFeatures(prev => prev ? { ...prev, features_layout: 'rows' } : prev)}
                            className={`px-3 py-1 text-sm rounded ${
                              traderFeatures.features_layout !== 'columns' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            Rows
                          </button>
                          <button
                            type="button"
                            onClick={() => setTraderFeatures(prev => prev ? { ...prev, features_layout: 'columns' } : prev)}
                            className={`px-3 py-1 text-sm rounded ${
                              traderFeatures.features_layout === 'columns' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            Columns
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-white rounded border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Feature 1</span>
                          <button
                            type="button"
                            onClick={() => setTraderFeatures(prev => prev ? { ...prev, feature1_enabled: !prev.feature1_enabled } : prev)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              traderFeatures.feature1_enabled ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              traderFeatures.feature1_enabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        <Input
                          type="text"
                          value={traderFeatures.feature1_title || ''}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, feature1_title: e.target.value } : prev)}
                          placeholder="Feature title"
                          className="text-sm mb-2"
                        />
                        <Textarea
                          value={traderFeatures.feature1_description || ''}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, feature1_description: e.target.value } : prev)}
                          placeholder="Feature description"
                          className="text-sm"
                          rows={2}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <img
                            src={
                              featureImagePreviews.feature1 ||
                              traderFeatures.feature1_image ||
                              trader.avatar_url ||
                              '/bally-Market-sign-200px.png'
                            }
                            alt="Feature 1"
                            className="w-12 h-12 rounded object-cover bg-gray-100"
                          />
                          <label
                            htmlFor="feature1-image-upload"
                            className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-xs hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            <span>Upload image</span>
                          </label>
                          <input
                            id="feature1-image-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              if (!file.type.startsWith('image/')) {
                                alert('File must be an image');
                                return;
                              }
                              setFeatureImageFiles(prev => ({ ...prev, feature1: file }));
                              setFeatureImagePreviews(prev => ({ ...prev, feature1: URL.createObjectURL(file) }));
                            }}
                          />
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Feature 2</span>
                          <button
                            type="button"
                            onClick={() => setTraderFeatures(prev => prev ? { ...prev, feature2_enabled: !prev.feature2_enabled } : prev)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              traderFeatures.feature2_enabled ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              traderFeatures.feature2_enabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        <Input
                          type="text"
                          value={traderFeatures.feature2_title || ''}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, feature2_title: e.target.value } : prev)}
                          placeholder="Feature title"
                          className="text-sm mb-2"
                        />
                        <Textarea
                          value={traderFeatures.feature2_description || ''}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, feature2_description: e.target.value } : prev)}
                          placeholder="Feature description"
                          className="text-sm"
                          rows={2}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <img
                            src={
                              featureImagePreviews.feature2 ||
                              traderFeatures.feature2_image ||
                              trader.avatar_url ||
                              '/bally-Market-sign-200px.png'
                            }
                            alt="Feature 2"
                            className="w-12 h-12 rounded object-cover bg-gray-100"
                          />
                          <label
                            htmlFor="feature2-image-upload"
                            className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-xs hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            <span>Upload image</span>
                          </label>
                          <input
                            id="feature2-image-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              if (!file.type.startsWith('image/')) {
                                alert('File must be an image');
                                return;
                              }
                              setFeatureImageFiles(prev => ({ ...prev, feature2: file }));
                              setFeatureImagePreviews(prev => ({ ...prev, feature2: URL.createObjectURL(file) }));
                            }}
                          />
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Feature 3</span>
                          <button
                            type="button"
                            onClick={() => setTraderFeatures(prev => prev ? { ...prev, feature3_enabled: !prev.feature3_enabled } : prev)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              traderFeatures.feature3_enabled ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              traderFeatures.feature3_enabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        <Input
                          type="text"
                          value={traderFeatures.feature3_title || ''}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, feature3_title: e.target.value } : prev)}
                          placeholder="Feature title"
                          className="text-sm mb-2"
                        />
                        <Textarea
                          value={traderFeatures.feature3_description || ''}
                          onChange={(e) => setTraderFeatures(prev => prev ? { ...prev, feature3_description: e.target.value } : prev)}
                          placeholder="Feature description"
                          className="text-sm"
                          rows={2}
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <img
                            src={
                              featureImagePreviews.feature3 ||
                              traderFeatures.feature3_image ||
                              trader.avatar_url ||
                              '/bally-Market-sign-200px.png'
                            }
                            alt="Feature 3"
                            className="w-12 h-12 rounded object-cover bg-gray-100"
                          />
                          <label
                            htmlFor="feature3-image-upload"
                            className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-xs hover:bg-blue-100 flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            <span>Upload image</span>
                          </label>
                          <input
                            id="feature3-image-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              if (!file.type.startsWith('image/')) {
                                alert('File must be an image');
                                return;
                              }
                              setFeatureImageFiles(prev => ({ ...prev, feature3: file }));
                              setFeatureImagePreviews(prev => ({ ...prev, feature3: URL.createObjectURL(file) }));
                            }}
                          />
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!traderFeatures || !trader.user_id) return;
                          setSavingFeatures(true);
                          try {
                            const updates: Partial<TraderFeatures> = {
                              feature1_enabled: traderFeatures.feature1_enabled,
                              feature1_title: traderFeatures.feature1_title,
                              feature1_description: traderFeatures.feature1_description,
                              feature1_image: traderFeatures.feature1_image,
                              feature2_enabled: traderFeatures.feature2_enabled,
                              feature2_title: traderFeatures.feature2_title,
                              feature2_description: traderFeatures.feature2_description,
                              feature2_image: traderFeatures.feature2_image,
                              feature3_enabled: traderFeatures.feature3_enabled,
                              feature3_title: traderFeatures.feature3_title,
                              feature3_description: traderFeatures.feature3_description,
                              feature3_image: traderFeatures.feature3_image,
                              features_section_title: traderFeatures.features_section_title,
                              features_layout: traderFeatures.features_layout
                            };

                            const uploadImage = async (key: 'feature1' | 'feature2' | 'feature3') => {
                              const file = featureImageFiles[key];
                              if (!file) return;
                              const path = `trader_features/${trader.user_id}/${key}_${Date.now()}_${file.name}`;
                              const { error: uploadError } = await supabase.storage
                                .from('avatars')
                                .upload(path, file, {
                                  cacheControl: '3600',
                                  upsert: true
                                });
                              if (uploadError) {
                                throw uploadError;
                              }
                              const { data: urlData } = supabase.storage
                                .from('avatars')
                                .getPublicUrl(path);
                              if (urlData?.publicUrl) {
                                updates[`${key}_image` as keyof TraderFeatures] = urlData.publicUrl as any;
                              }
                            };

                            await uploadImage('feature1');
                            await uploadImage('feature2');
                            await uploadImage('feature3');

                            // Pass true to skip auth check since admin is updating on behalf of trader
                            const result = await traderService.updateTraderFeatures(trader.user_id, updates, true);

                            setFeatureImageFiles({ feature1: null, feature2: null, feature3: null });
                            setFeatureImagePreviews({ feature1: null, feature2: null, feature3: null });

                            if (result) {
                              setTraderFeatures(prev => (prev ? { ...prev, ...result } : result));
                            }

                            alert('✅ Features saved successfully!');
                          } catch (err) {
                            console.error('Error saving features:', err);
                            alert('Failed to save features');
                          } finally {
                            setSavingFeatures(false);
                          }
                        }}
                        disabled={savingFeatures}
                        className="w-full"
                      >
                        {savingFeatures ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" /> Save Features</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Keywords Display and Regeneration */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Search Keywords
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateKeywords}
                    disabled={isRegeneratingKeywords}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRegeneratingKeywords ? 'animate-spin' : ''}`} />
                    {isRegeneratingKeywords ? 'Generating...' : 'Regenerate'}
                  </Button>
                </div>
                
                {trader.keywords && trader.keywords.length > 0 ? (
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                    {trader.keywords.map((keyword: string, idx: number) => (
                      <span
                        key={idx}
                        className="inline-block px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500 text-center">
                    No keywords generated yet. Click "Regenerate" to generate keywords.
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="px-4 py-2"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="px-4 py-2"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  <span>{trader?.id ? 'Save Changes' : 'Add Trader'}</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditTraderForm;
